/**
 * T6001 · HMR captured-handle cleanup.
 *
 * SPEC ANCHOR: `specs/009-data-fetching-audit/05-new-infra-sdk-changes.html`
 * §L1283 — HMR replacement leaves the OLD module's closures running. Without
 * ORDERED cleanup, every Fast Refresh in dev accumulates dead sweeper
 * intervals + dangling IntersectionObservers + parallel-subscriber sets on
 * the same WS, surfacing as `cannot update state on unmounted component`
 * errors or silent writes to a stale QueryClient.
 *
 * INDUSTRY-STANDARD SINGLETON IS NECESSARY BUT NOT SUFFICIENT
 * -----------------------------------------------------------
 * `globalThis.__morfi_registry ??= createRegistry()` collapses double
 * imports / Turbopack code-split misses / Fast Refresh swaps to ONE
 * instance — but it does NOT protect against the OLD module's closures
 * continuing to run. The sweeper `setInterval`, the shared
 * `IntersectionObserver`, and the `Map<Element, Acquisition>` are captured
 * in the original module's scope.
 *
 * FIX — every long-lived handle pinned on `globalThis`, torn down BEFORE
 * the new instance is constructed, in this exact order:
 *   1. `clearInterval(globalThis.__morfi_registry_sweepHandle)` — STOP the
 *      sweeper FIRST so it can't fire mid-teardown.
 *   2. iterate `__morfi_registry_observer.elementMap.entries()` and call
 *      `acquisition.release()` on EACH (drops GC-roots from old Acquisition
 *      closures), THEN `observer.disconnect()`, THEN drop the Map.
 *   3. iterate `__morfi_registry.subjectCallbacks` + clear; tear down all
 *      pending grace timers.
 *   4. construct the new instance, pin all handles fresh.
 *
 * DEV-ONLY LOUD-FAILURE GUARD
 * ---------------------------
 * `assertSingleConstruction()` throws on second invocation in the same
 * realm — loud failure with immediate stack trace beats silent doubled-WS.
 * Production builds compile the branch out (the `process.env.NODE_ENV`
 * literal is replaced at build time).
 *
 * `destroy()` IS TEST-ONLY
 * ------------------------
 * Production code MUST NOT call `registry.destroy()`. It exists so vitest
 * can run multiple registry instances in one process without the
 * `__morfi_registry_constructed` guard leaking. Lint rule
 * `no-destroy-in-prod` (Biome plugin) rejects `.destroy()` outside
 * `*.test.ts` / `*.test.tsx`.
 */

/** Minimal shape the cleanup needs from a registry instance. */
export interface HmrRegistryHandle {
  /** Tear down EVERY internal handle. TEST-ONLY in production. */
  destroy(): void;
}

/** Map binding observed Elements to their Acquisition tear-down closure. */
export interface HmrObserverState {
  readonly observer: IntersectionObserver;
  readonly elementMap: Map<Element, { release: () => void }>;
}

declare global {
  var __morfi_registry: HmrRegistryHandle | undefined;
  var __morfi_registry_sweepHandle: ReturnType<typeof setInterval> | undefined;
  var __morfi_registry_observer: HmrObserverState | undefined;
  var __morfi_registry_constructed: boolean | undefined;
  var __morfi_registry_generation: number | undefined;
}

/**
 * Tear down the OLD registry instance's long-lived handles BEFORE a new
 * instance is constructed. Idempotent — safe to call when nothing is
 * pinned (first construction).
 *
 * ORDER IS LOAD-BEARING. Each step is annotated with the failure mode it
 * prevents — do not reorder without re-reading the spec anchor above.
 */
export function teardownOldRegistry(): void {
  // 1. Stop the sweeper FIRST so it can't fire mid-teardown. A late tick
  //    after `observer.disconnect()` would attempt to dispatch to a
  //    nulled callback — silent crash in the dev console.
  if (globalThis.__morfi_registry_sweepHandle !== undefined) {
    clearInterval(globalThis.__morfi_registry_sweepHandle);
    globalThis.__morfi_registry_sweepHandle = undefined;
  }

  // 2. Release every observed Acquisition BEFORE `observer.disconnect()`.
  //    The Map GC-roots Acquisition objects whose `release` closures
  //    reference the OLD module's callbacks map + natsClient.subscribe
  //    binding. Without this, the OLD subscribers stay alive in parallel
  //    on the same WS — every event arrives twice (or N×) per HMR.
  if (globalThis.__morfi_registry_observer !== undefined) {
    for (const [, acquisition] of globalThis.__morfi_registry_observer.elementMap) {
      try {
        acquisition.release();
      } catch {
        // Swallow per-element to keep cleanup going; an Acquisition
        // failing to release must not block the rest of the sweep.
      }
    }
    globalThis.__morfi_registry_observer.observer.disconnect();
    globalThis.__morfi_registry_observer.elementMap.clear();
    globalThis.__morfi_registry_observer = undefined;
  }

  // 3. Drop callbacks + cancel grace timers on the OLD instance. The
  //    registry's `destroy()` is the canonical teardown surface; we call
  //    through it so the OLD instance's internal Maps are also dropped.
  if (globalThis.__morfi_registry !== undefined) {
    try {
      globalThis.__morfi_registry.destroy();
    } catch {
      // Tolerate destroy throws — first construction will set
      // __morfi_registry to a fresh instance regardless.
    }
    globalThis.__morfi_registry = undefined;
  }

  // 4. Reset the constructed-twice guard so the next createRegistry()
  //    call passes the dev-only assertion.
  globalThis.__morfi_registry_constructed = false;
}

/**
 * Dev-only loud-failure guard. Throws on second invocation in the same
 * realm to catch duplicate-import / HMR-replacement bugs that bypassed
 * `teardownOldRegistry()`. Production builds compile the branch out.
 */
export function assertSingleConstruction(): void {
  if (process.env.NODE_ENV === 'production') return;
  if (globalThis.__morfi_registry_constructed === true) {
    throw new Error(
      'Registry constructed twice — duplicate import or HMR bug. ' +
        'See specs/009-data-fetching-audit/05-new-infra-sdk-changes.html L1283.',
    );
  }
  globalThis.__morfi_registry_constructed = true;
  globalThis.__morfi_registry_generation = (globalThis.__morfi_registry_generation ?? 0) + 1;
}

/**
 * Pin a freshly-constructed registry on globalThis so the next HMR cycle
 * can reach it from `teardownOldRegistry()`. Called immediately after the
 * factory constructs a new instance.
 */
export function pinRegistry(registry: HmrRegistryHandle): void {
  globalThis.__morfi_registry = registry;
}

/**
 * Pin the global IntersectionObserver + elementMap so cleanup can find it.
 * Called by the registry's IntersectionObserver init path.
 */
export function pinObserver(state: HmrObserverState): void {
  globalThis.__morfi_registry_observer = state;
}

/**
 * Pin the sweep interval so HMR cleanup can stop it. Called immediately
 * after `setInterval(sweep, ...)`.
 */
export function pinSweepHandle(handle: ReturnType<typeof setInterval>): void {
  globalThis.__morfi_registry_sweepHandle = handle;
}

/** Devtool gauge — read by the WS-count panel to display the HMR cycle count. */
export function readGeneration(): number {
  return globalThis.__morfi_registry_generation ?? 0;
}

/**
 * Test seam — wipes every globalThis handle. Production code MUST NOT
 * call this; it exists so vitest can run multiple registry instances in
 * one process without `__morfi_registry_constructed` leaking. Mirrors the
 * `registry.destroy()` test-only API.
 */
export function _resetHmrGlobalsForTest(): void {
  globalThis.__morfi_registry = undefined;
  globalThis.__morfi_registry_sweepHandle = undefined;
  globalThis.__morfi_registry_observer = undefined;
  globalThis.__morfi_registry_constructed = false;
  globalThis.__morfi_registry_generation = 0;
}
